/**
 * Bookkeeping. The three queues a person clears, per entity: rule hits, AI proposals, anomalies.
 *
 * Two things this screen is careful about:
 *
 *  1. **Every outcome is the API's, per row.** All three writes reply with a list, one row per
 *     thing asked about (plan 8a decision 3), and that is how they are shown: a refused rule hit
 *     stays in its row with the reason beside it; an approval that was recorded but did not run
 *     says so in its row. Nothing on this page says an action ran unless the API said
 *     `executed: true` — "Approved" alone means a decision was recorded, which is a different
 *     fact from the classification having been written.
 *  2. **It shows money and counts; it never computes them.** Amounts go through `formatMoney`
 *     and nothing else (CLAUDE.md 3.1). The totals in the summary line are the queues' SQL
 *     `total`s, read as sent. The only number this file produces is how many rows are ticked.
 */

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  api,
  type AiProposalItem,
  type AnomalyItem,
  type BookkeepingQueues,
  type BulkDecisionResult,
  type QueueTransaction,
  type RuleHitItem,
  type RuleHitResult,
} from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { batchSentence, decisionLabel } from './bookkeeping-view.js';
import { formatMoney } from '../../money.js';

/** "1 rule hit", "2 rule hits". Counts the API sent, never cents. */
function count(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** "showing 200 of 412" when a queue was capped at the API's `cap`; nothing otherwise. */
function Capped({ shown, total }: { shown: number; total: number }): ReactNode {
  if (total <= shown) return null;
  return (
    <span className="muted small">
      showing {shown} of {total}
    </span>
  );
}

/** A request that failed as a whole — nothing in it was recorded. */
function RequestError({ error }: { error: unknown }): ReactNode {
  if (!error) return null;
  return (
    <p className="error">
      {error instanceof ApiError && error.killSwitch
        ? 'The platform is in READ-ONLY mode. Nothing was recorded.'
        : (error as Error).message}
    </p>
  );
}

/** Date, vendor, and amount of the line a proposal or an anomaly is about. */
function LineSummary({ t }: { t: QueueTransaction }): ReactNode {
  return (
    <>
      <span className="nowrap">{t.txn_date}</span>
      {' · '}
      {t.vendor_name ?? <span className="muted">no vendor</span>}
      {' · '}
      <span className="num">{formatMoney(t.amount_cents)}</span>
      {t.description && <div className="muted small">{t.description}</div>}
    </>
  );
}

export function BookkeepingPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();
  const queryClient = useQueryClient();

  const queues = useQuery({
    queryKey: ['finance', 'bookkeeping', entityId],
    enabled: entityId !== null,
    queryFn: () => api.get<BookkeepingQueues>(`/api/finance/bookkeeping?entity=${entityId}`),
  });

  // After any write: the queues, the counts beside the entity names, and everything else that
  // reads a classification or an approval. Refetched, never patched locally — the next render
  // shows what the database holds, not what this page hoped it would.
  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['finance', 'bookkeeping'] });
    void queryClient.invalidateQueries({ queryKey: ['finance', 'transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['entities', 'finance'] });
    void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    void queryClient.invalidateQueries({ queryKey: ['control-center'] });
  }

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = queues.data;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Bookkeeping</h1>
          <p className="subtitle">
            Rule hits, AI proposals, and anomalies — the three queues a person clears, per entity.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
      </div>

      {(queues.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {queues.isError && <p className="error">Could not load the queues: {queues.error.message}</p>}

      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && (
        <>
          <p className="muted queue-summary">
            {count(data.rule_hits.total_proposals, 'rule hit')} ·{' '}
            {count(data.rule_hits.total_conflicts, 'rule conflict')} ·{' '}
            {count(data.ai_proposals.total, 'AI proposal')} ·{' '}
            {count(data.anomalies.total, 'open anomaly', 'open anomalies')}
          </p>

          <RuleHits key={`rules-${entityId}`} queue={data.rule_hits} onChanged={invalidate} />
          <AiProposals key={`ai-${entityId}`} queue={data.ai_proposals} onChanged={invalidate} />
          <Anomalies key={`anomalies-${entityId}`} queue={data.anomalies} onChanged={invalidate} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Selection. A set of ids and a header checkbox that is indeterminate when some are ticked — the
// DOM property has no attribute, so it is set through a ref.
// ---------------------------------------------------------------------------------------------

function useSelection(ids: string[]) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // Rows leave the queue when they are classified or decided; a selection must not keep ids the
  // table no longer shows, or "Approve selected (3)" would count rows nobody can see.
  const present = new Set(ids);
  const current = new Set([...selected].filter((id) => present.has(id)));
  const all = ids.length > 0 && current.size === ids.length;
  const some = current.size > 0 && !all;

  return {
    selected: current,
    has: (id: string) => current.has(id),
    toggle: (id: string) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    toggleAll: () => setSelected(all ? new Set() : new Set(ids)),
    clear: () => setSelected(new Set()),
    all,
    some,
  };
}

function HeaderCheckbox({
  all,
  some,
  onChange,
  label,
}: {
  all: boolean;
  some: boolean;
  onChange: () => void;
  label: string;
}): ReactNode {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);
  return <input ref={ref} type="checkbox" checked={all} onChange={onChange} aria-label={label} />;
}

// ---------------------------------------------------------------------------------------------
// Rule hits.
// ---------------------------------------------------------------------------------------------

function RuleHits({
  queue,
  onChanged,
}: {
  queue: BookkeepingQueues['rule_hits'];
  onChanged: () => void;
}): ReactNode {
  const proposals = queue.items.filter((h) => !h.is_conflict);
  const conflicts = queue.items.filter((h) => h.is_conflict);
  const selection = useSelection(proposals.map((h) => h.transaction_id));
  const [results, setResults] = useState<Map<string, RuleHitResult>>(() => new Map());

  const approve = useMutation({
    mutationFn: (transactionIds: string[]) =>
      api.post<{ results: RuleHitResult[] }>('/api/bookkeeping/rule-hits/approve', {
        transactionIds,
      }),
    onSuccess: ({ results: rows }) => {
      setResults((prev) => {
        const next = new Map(prev);
        for (const row of rows) next.set(row.transactionId, row);
        return next;
      });
      selection.clear();
      onChanged();
    },
  });

  const last = approve.data?.results ?? [];
  const classified = last.filter((r) => r.status === 'classified').length;
  const refused = last.filter((r) => r.status === 'refused').length;
  // Counted separately rather than as "everything that is not classified". A refusal is a rule
  // saying no and a failure is something breaking, and rolling them together is exactly what
  // ROADMAP §7's batch defect did to the person reading this line.
  const failed = last.filter((r) => r.status === 'failed').length;

  return (
    <section className="queue">
      <header className="queue-head">
        <h2>Rule hits</h2>
        <Capped shown={proposals.length} total={queue.total_proposals} />
        <div className="spacer" />
        {proposals.length > 0 && (
          <button
            type="button"
            className="primary"
            disabled={selection.selected.size === 0 || approve.isPending}
            onClick={() => approve.mutate([...selection.selected])}
          >
            {approve.isPending ? 'Approving…' : `Approve selected (${selection.selected.size})`}
          </button>
        )}
      </header>

      <RequestError error={approve.error} />
      {last.length > 0 && (
        <p className="muted small">
          Last batch: {count(classified, 'line')} classified
          {refused > 0 ? `, ${refused} refused — the reasons are in the rows below` : ''}
          {failed > 0
            ? `, ${failed} could not be processed — nothing was recorded for those, and they can be
               selected again`
            : ''}
          .
        </p>
      )}

      {proposals.length === 0 ? (
        <div className="card empty">
          <h2>Nothing to approve.</h2>
          <p>No active vendor rule matches an unclassified line for this entity.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>
                <HeaderCheckbox
                  all={selection.all}
                  some={selection.some}
                  onChange={selection.toggleAll}
                  label="Select all rule hits"
                />
              </th>
              <th>Date</th>
              <th>Vendor</th>
              <th className="num">Amount</th>
              <th>Canonical line</th>
              <th>Property</th>
              <th>CapEx</th>
              <th>Rule</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {proposals.map((h) => (
              <RuleHitRow
                key={h.transaction_id}
                hit={h}
                checked={selection.has(h.transaction_id)}
                onToggle={() => selection.toggle(h.transaction_id)}
                result={results.get(h.transaction_id)}
              />
            ))}
          </tbody>
        </table>
      )}

      <h3 className="queue-subhead">
        Conflicts <Capped shown={conflicts.length} total={queue.total_conflicts} />
      </h3>
      {conflicts.length === 0 ? (
        <p className="muted small">No conflicts.</p>
      ) : (
        <table className="table compact">
          <thead>
            <tr>
              <th>Date</th>
              <th>Vendor</th>
              <th className="num">Amount</th>
              <th>Competing rules</th>
            </tr>
          </thead>
          <tbody>
            {conflicts.map((h) => (
              <tr key={h.transaction_id}>
                <td className="nowrap">{h.txn_date}</td>
                <td>
                  {h.vendor_name ?? <span className="muted">—</span>}
                  {h.description && <div className="muted small">{h.description}</div>}
                </td>
                <td className="num">{formatMoney(h.amount_cents)}</td>
                <td>
                  <dl className="kv conflict-rules">
                    {h.rule_ids.map((ruleId, i) => (
                      <Fragment key={ruleId}>
                        <dt>
                          <code>{ruleId.slice(0, 8)}</code>
                        </dt>
                        <dd>
                          <code>{h.conflicting_canonical_keys?.[i] ?? '—'}</code>
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                  <div className="muted small">A conflict proposes nothing. Edit a rule.</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function RuleHitRow({
  hit,
  checked,
  onToggle,
  result,
}: {
  hit: RuleHitItem;
  checked: boolean;
  onToggle: () => void;
  result: RuleHitResult | undefined;
}): ReactNode {
  return (
    <tr
      className={
        result?.status === 'refused' || result?.status === 'failed' ? 'refused' : undefined
      }
    >
      <td>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${hit.vendor_name ?? hit.transaction_id}`}
        />
      </td>
      <td className="nowrap">{hit.txn_date}</td>
      <td>
        {hit.vendor_name ?? <span className="muted">—</span>}
        {hit.description && <div className="muted small">{hit.description}</div>}
      </td>
      <td className="num">{formatMoney(hit.amount_cents)}</td>
      <td>
        {hit.canonical_name ?? <span className="muted">—</span>}
        {hit.canonical_key && (
          <div className="muted small">
            <code>{hit.canonical_key}</code>
          </div>
        )}
      </td>
      <td>{hit.property_name ?? <span className="muted">—</span>}</td>
      <td>{hit.is_capex ? 'CapEx' : <span className="muted">—</span>}</td>
      <td>
        {hit.rule_id ? (
          <code title={hit.rule_id}>{hit.rule_id.slice(0, 8)}</code>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        {result?.status === 'classified' && (
          <span className="status status-approved">Classified</span>
        )}
        {result?.status === 'refused' && (
          <>
            <span className="status status-conflict">Refused</span>
            <div className="small">{result.error}</div>
          </>
        )}
        {/*
          Its own word, not "Refused". A refusal is a rule saying no about THIS line and the owner
          should read it; a failure says nothing about the line at all, and the useful thing to
          know is that it can simply be tried again.
        */}
        {result?.status === 'failed' && (
          <>
            <span className="status status-conflict">Not processed</span>
            <div className="small">{result.error}</div>
          </>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------------------------
// AI proposals.
// ---------------------------------------------------------------------------------------------

/**
 * The words for one bulk-decision row. Checked in the order the API's own codes rank them: a
 * 409 or 404 never decided anything, whatever `status` says about the approval's existing state;
 * then the decision, and for an approval, whether the action actually ran.
 */
function AiProposals({
  queue,
  onChanged,
}: {
  queue: BookkeepingQueues['ai_proposals'];
  onChanged: () => void;
}): ReactNode {
  const selection = useSelection(queue.items.map((p) => p.id));
  const [results, setResults] = useState<Map<string, BulkDecisionResult>>(() => new Map());

  const decide = useMutation({
    mutationFn: (body: { ids: string[]; decision: 'approve' | 'reject' }) =>
      api.post<{ results: BulkDecisionResult[] }>('/api/approvals/decisions', body),
    onSuccess: ({ results: rows }) => {
      setResults((prev) => {
        const next = new Map(prev);
        for (const row of rows) next.set(row.id, row);
        return next;
      });
      selection.clear();
      onChanged();
    },
  });

  const last = decide.data?.results ?? [];

  // Both the sentence and the row labels bucket a result through `bucketOf`, so they cannot
  // disagree about an outcome they describe a centimetre apart (`bookkeeping-view.ts`).
  //
  // `stillLabelled` is how many rows on screen RIGHT NOW carry a label from this batch. A row
  // approved and executed leaves the queue on the refetch a moment later, so the sentence's
  // pointer used to send a reader looking at nothing for the commonest outcome there is.
  const stillLabelled = queue.items.filter((p) => results.has(p.id)).length;
  const sentence = batchSentence(last, stillLabelled);

  return (
    <section className="queue">
      <header className="queue-head">
        <h2>AI proposals</h2>
        <Capped shown={queue.items.length} total={queue.total} />
        <div className="spacer" />
        {queue.items.length > 0 && (
          <>
            <button
              type="button"
              className="primary"
              disabled={selection.selected.size === 0 || decide.isPending}
              onClick={() => decide.mutate({ ids: [...selection.selected], decision: 'approve' })}
            >
              {decide.isPending ? 'Deciding…' : `Approve selected (${selection.selected.size})`}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={selection.selected.size === 0 || decide.isPending}
              onClick={() => decide.mutate({ ids: [...selection.selected], decision: 'reject' })}
            >
              Reject selected
            </button>
          </>
        )}
      </header>

      <RequestError error={decide.error} />
      {sentence !== null && <p className="muted small">{sentence}</p>}

      {queue.items.length === 0 ? (
        <div className="card empty">
          <h2>Nothing to approve.</h2>
          <p>The Finance Agent has no proposal waiting on this entity&rsquo;s lines.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>
                <HeaderCheckbox
                  all={selection.all}
                  some={selection.some}
                  onChange={selection.toggleAll}
                  label="Select all AI proposals"
                />
              </th>
              <th>Proposal</th>
              <th>Line</th>
              <th>Confidence</th>
              <th>Created</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {queue.items.map((p) => (
              <AiProposalRow
                key={p.id}
                proposal={p}
                checked={selection.has(p.id)}
                onToggle={() => selection.toggle(p.id)}
                result={results.get(p.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function AiProposalRow({
  proposal,
  checked,
  onToggle,
  result,
}: {
  proposal: AiProposalItem;
  checked: boolean;
  onToggle: () => void;
  result: BulkDecisionResult | undefined;
}): ReactNode {
  const label = result ? decisionLabel(result) : null;
  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${proposal.title}`}
        />
      </td>
      <td>
        {proposal.title}
        <div className="muted small">
          {proposal.agent_name} ·{' '}
          {/* MODIFY lives in the Approval Center; `?id=` opens this proposal's drawer directly. */}
          <Link to={`/approvals?id=${encodeURIComponent(proposal.id)}`}>
            Open in Approvals to modify
          </Link>
        </div>
      </td>
      <td>
        <LineSummary t={proposal.transaction} />
      </td>
      <td>{proposal.recommendation.confidence ?? <span className="muted">—</span>}</td>
      <td className="muted nowrap">{new Date(proposal.created_at).toLocaleString()}</td>
      <td>
        {label && (
          <>
            <span className={label.tone}>{label.text}</span>
            {label.error && <div className="small">{label.error}</div>}
          </>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------------------------
// Anomalies.
// ---------------------------------------------------------------------------------------------

const SEVERITY_CHIP: Record<AnomalyItem['severity'], string> = {
  low: 'chip',
  medium: 'chip chip-warn',
  high: 'chip chip-danger',
};

type ResolveOutcome =
  { kind: 'resolved' } | { kind: 'already_resolved' } | { kind: 'error'; message: string };

function Anomalies({
  queue,
  onChanged,
}: {
  queue: BookkeepingQueues['anomalies'];
  onChanged: () => void;
}): ReactNode {
  const [results, setResults] = useState<Map<string, ResolveOutcome>>(() => new Map());

  function record(id: string, outcome: ResolveOutcome): void {
    setResults((prev) => new Map(prev).set(id, outcome));
  }

  return (
    <section className="queue">
      <header className="queue-head">
        <h2>Anomalies</h2>
        <Capped shown={queue.items.length} total={queue.total} />
      </header>

      {queue.items.length === 0 ? (
        <div className="card empty">
          <h2>No open anomalies.</h2>
          <p>Nothing the Finance Agent flagged on this entity is waiting for review.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Severity</th>
              <th>Summary</th>
              <th>Line</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {queue.items.map((a) => (
              <AnomalyRow
                key={a.id}
                anomaly={a}
                outcome={results.get(a.id)}
                onOutcome={(outcome) => {
                  record(a.id, outcome);
                  if (outcome.kind !== 'error') onChanged();
                }}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function AnomalyRow({
  anomaly,
  outcome,
  onOutcome,
}: {
  anomaly: AnomalyItem;
  outcome: ResolveOutcome | undefined;
  onOutcome: (outcome: ResolveOutcome) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');

  const resolve = useMutation({
    mutationFn: (body: { note?: string }) =>
      api.post<{ anomaly: unknown }>(`/api/anomalies/${anomaly.id}/resolve`, body),
    onSuccess: () => {
      setOpen(false);
      onOutcome({ kind: 'resolved' });
    },
    onError: (err) => {
      // 409: someone else got there first. The row will leave on refetch; the person who clicked
      // learns it was already handled rather than believing they handled it.
      if (err instanceof ApiError && err.status === 409) {
        setOpen(false);
        onOutcome({ kind: 'already_resolved' });
        return;
      }
      onOutcome({
        kind: 'error',
        message:
          err instanceof ApiError && err.killSwitch
            ? 'The platform is in READ-ONLY mode. Nothing was recorded.'
            : err.message,
      });
    },
  });

  const done = outcome?.kind === 'resolved' || outcome?.kind === 'already_resolved';

  return (
    <tr>
      <td>
        <code>{anomaly.kind}</code>
      </td>
      <td>
        <span className={SEVERITY_CHIP[anomaly.severity] ?? 'chip'}>{anomaly.severity}</span>
      </td>
      <td>
        {anomaly.summary}
        {anomaly.confidence_level && (
          <div className="muted small">confidence {anomaly.confidence_level}</div>
        )}
      </td>
      <td>
        {anomaly.transaction_id && anomaly.txn_date && anomaly.amount_cents ? (
          <LineSummary
            t={{
              id: anomaly.transaction_id,
              txn_date: anomaly.txn_date,
              amount_cents: anomaly.amount_cents,
              description: anomaly.description,
              doc_type: null,
              doc_number: null,
              vendor_name: anomaly.vendor_name,
            }}
          />
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td className="muted nowrap">{new Date(anomaly.created_at).toLocaleString()}</td>
      <td>
        {outcome?.kind === 'resolved' && <span className="status status-approved">Resolved</span>}
        {outcome?.kind === 'already_resolved' && <span className="status">Already resolved</span>}
        {!done && !open && (
          <button type="button" className="secondary" onClick={() => setOpen(true)}>
            Resolve
          </button>
        )}
        {!done && open && (
          <div className="resolve-form">
            <input
              type="text"
              value={note}
              placeholder="Note (optional)"
              aria-label="Resolution note"
              onChange={(e) => setNote(e.target.value)}
              disabled={resolve.isPending}
            />
            <div className="actions">
              <button
                type="button"
                className="primary"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate(note.trim() ? { note: note.trim() } : {})}
              >
                {resolve.isPending ? 'Resolving…' : 'Resolve'}
              </button>
              <button
                type="button"
                className="secondary"
                disabled={resolve.isPending}
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {outcome?.kind === 'error' && <div className="error small">{outcome.message}</div>}
      </td>
    </tr>
  );
}
